<template>
  <DesignCollapseTemplate collapseId="stamps" class="mb-3">
    <template slot="header">Stamps</template>
    <template slot="content">
      <b-form>
        <b-form-group label-class="punch-card-form-label" label="Number of Stamps">
          <b-dropdown id="dropdown-punch-card-stamps-number" :text="selectedStampsCountText" class="w-100"
            :toggle-class="['text-left bg-transparent border text-dark shadow-none w-100', { 'unselected': !campaignData.design.number_of_stamps, 'border-danger': $v.number_of_stamps.$errors.length || !!apiErrors.number_of_stamps }]"
            menu-class="w-100 p-0" no-caret>
            <b-dropdown-item v-for="(stampCount, stampIndex) in numberOfStampsArray"
              :key="`option-punch-card-campaign-stampCount-${stampIndex}`"
              :id="`option-punch-card-campaign-stampCount-${stampIndex}`" @click="setNumberOfStamps(stampCount)">
              {{ stampCount }}
            </b-dropdown-item>
          </b-dropdown>
          <b-form-invalid-feedback :force-show="$v.number_of_stamps.$errors.length > 0 || !!apiErrors.number_of_stamps">
            <template v-if="!!apiErrors.number_of_stamps">{{ apiErrors.number_of_stamps }}</template>
            <p v-else class="mb-0" v-for="error in $v.number_of_stamps.$errors" :key="error.$uid">{{ error.$message }}
            </p>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label-class="punch-card-form-label" label="Stamp Icon" class="mb-0">
        </b-form-group>
        <b-form-group label-class="punch-card-form-label d-flex align-items-center" class="mb-0">
          <template slot="label">
            <img src="@/assets/images/icons/info.svg" alt="Info" :style="{ opacity: '0.5' }"> <span
              class="ml-2 text-muted">Select an icon or upload your own image to represent a stamp.</span>
          </template>
          <div class="stamps-container border d-flex align-items-center">
            <div class="stamps-wrapper align-items-center justify-content-between">
              <b-button v-for="(stamp, index) in stamps" :key="`button-stamp-icon-${index}`"
                :id="`button-stamp-icon-${index}`"
                :class="['bg-transparent h-100 shadow-none', { '-dotted': stamp.label === 'upload', '-selected': selectedStamp === stamp.label || campaignData.design.stamp_preset === stamp.label }]"
                @click="stamp.label === 'upload' ? openFileBrowser() : selectedStamp = stamp.label">
                <img :src="stamp.icon || campaignData.design.stamp_icon" :alt="stamp.label" class="img-fluid">
              </b-button>
            </div>
          </div>
          <b-form-invalid-feedback
            :force-show="$v.stamp_icon.$errors.length > 0 || !!uploadedImgWarning || !!apiErrors.stamp_icon">
            <template v-if="!!uploadedImgWarning || !!apiErrors.stamp_icon">{{ uploadedImgWarning ||
              apiErrors.stamp_icon }}</template>
            <template v-else>
              <p class="mb-0" v-for="error in $v.stamp_icon.$errors" :key="error.$uid">{{ error.$message }}</p>
            </template>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-form>

      <ImageUpload ref="newStampImgUpload" :extensions="['.png']" :enableCropping="true" @onPreviewImage="setImage"
        @hasWarning="setWarningMessage" :fileOnly="true" aspectRatio="square" :resetImageUploader="resetImageUploader"
        :imgUploadId="`stampImgUploader-${source}`" :key="`stampImgUploader-${source}`" />
    </template>
  </DesignCollapseTemplate>
</template>

<script>
import { computed, defineComponent, getCurrentInstance, onMounted, onBeforeUnmount, ref, watch } from "@vue/composition-api";
import DesignCollapseTemplate from "./DesignCollapseTemplate.vue";
import { mapFields } from "vuex-map-fields";
import { stampsPreset, fieldRequiredErrorMessage, isValidUrl } from "../../punch.card.utilities";
import useVuelidate from "@vuelidate/core";
import { helpers, maxValue, minValue, required } from "@vuelidate/validators";
import ImageUpload from "@/components/common/ImageUpload.vue";
const image2base64 = require('image-to-base64');

export default defineComponent({
  props: {
    source: {
      type: String,
      default: "create"
    }
  },
  components: { DesignCollapseTemplate, ImageUpload },
  computed: {
    ...mapFields("punchCard", ["campaignData"])
  },
  setup(_, { root }) {
    const currentInstance = getCurrentInstance();

    const form = computed(() => currentInstance.proxy.campaignData.design || {});

    const openFileBrowser = () => currentInstance.proxy.$refs["newStampImgUpload"].$refs["fileInput"].click();

    const stamps = ref([...stampsPreset]);

    const selectedStamp = ref(null);

    const rules = {
      number_of_stamps: {
        required: helpers.withMessage(fieldRequiredErrorMessage, required),
        minValue: minValue(3),
        maxValue: maxValue(30)
      },
      stamp_icon: {
        required: helpers.withMessage(fieldRequiredErrorMessage, required)
      }
    };

    const $v = useVuelidate(rules, form);

    onMounted(() => {
      root.$on("validateForm", async () => {
        $v.value.$touch();

        if (
          $v.value.$dirty &&
          $v.value.$invalid &&
          currentInstance.proxy.campaignData.active === "design"
        ) {
          currentInstance.proxy.campaignData.designVisibility.stamps = true;
        }

        resetImgWarning();
      });
    });

    onBeforeUnmount(() => {
      root.$off("validateForm");
    });

    watch(
      () => selectedStamp.value,
      async (value) => {
        const selectedStampIcon = stamps.value.find(({ label }) => label === value) || null;
        currentInstance.proxy.campaignData.design.stamp_preset = value;

        if (value) {
          const selectImagePath = selectedStampIcon && selectedStampIcon.icon;

          currentInstance.proxy.campaignData.design.stamp_icon = selectImagePath;

          if (!isValidUrl(selectImagePath))
            currentInstance.proxy.campaignData.design.stamp_base64 = await image2base64(selectImagePath);
        }
      });

    const setImage = (image) => {
      selectedStamp.value = null;

      if (stamps.value.map(({ label }) => label).includes("custom")) {
        stamps.value.find(({ label }) => label === "custom").icon = image;
      } else {
        stamps.value.splice(1, 0, { label: "custom", icon: image });
      }

      currentInstance.proxy.$nextTick(() => selectedStamp.value = "custom");
    };

    const uploadedImgWarning = ref(null);

    const resetImageUploader = ref(false);

    const resetImgWarning = () => {
      uploadedImgWarning.value = null;
      resetImageUploader.value = true;
    };

    const setWarningMessage = (message) => {
      uploadedImgWarning.value = message;
      resetImageUploader.value = false;
    };

    const apiErrors = computed(() => root.$store.state.punchCard.errors || {});

    const numberOfStampsArray = Array.from({ length: 28 }, (_, i) => i + 3);

    const selectedStampsCountText = computed(() => {
      if (currentInstance.proxy.campaignData.design.number_of_stamps > 0) return currentInstance.proxy.campaignData.design.number_of_stamps.toString();

      return "Select Number of Stamps";
    });

    const setNumberOfStamps = (value) => {
      if (currentInstance.proxy.campaignData.design.milestones.length > 0)
        root.$emit("bv::show::modal", "punch-card-campaign-clear-rewards-modal", value);
      else
        currentInstance.proxy.campaignData.design.number_of_stamps = value;
    };

    return {
      stamps,
      openFileBrowser,
      selectedStamp,
      $v,
      setImage,
      uploadedImgWarning,
      setWarningMessage,
      resetImageUploader,
      apiErrors,
      numberOfStampsArray,
      selectedStampsCountText,
      setNumberOfStamps
    }
  },
  watch: {
    campaignData: {
      deep: true,
      handler() {
        if (this.$store.state.punchCard.campaignData.design.stamp_preset === "custom") {
          const custom = this.stamps.find(({ label }) => label === "custom");
          const custom_icon = { label: "custom", icon: this.$store.state.punchCard.campaignData.design.stamp_icon };

          if (!custom)
            this.stamps.splice(1, 0, custom_icon);
          else
            this.stamps[1] = custom_icon;
        }
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.stamps-container {
  border-radius: 10px;
  border-color: #D8DCDE !important;
  padding: 20px 14px;

  .stamps-wrapper {
    max-width: 470px;
    display: grid;
    grid-template-columns: repeat(7, 56px);
    grid-template-rows: repeat(2, 56px);
    gap: 12px;

    [id^="button-stamp-icon-"] {
      border-color: #D8DCDE !important;

      &>img {
        max-width: 33px;
        max-height: 33px;
        object-fit: contain;
      }

      &.-dotted {
        border-style: dashed;
        border-color: #FF6B00 !important;
      }

      &.-selected {
        border-color: #FF6B00 !important;
        background-color: rgba($color: #FF6B00, $alpha: 0.05) !important;
      }
    }
  }
}
</style>

<style lang="scss">
#dropdown-punch-card-stamps-number {
  .dropdown-toggle {
    height: 44px;
    background: transparent url("~@/assets/images/caret-down-2.svg") no-repeat right 12px center;
    padding-right: 2.75rem;

    &.unselected {
      color: #87929D !important;
    }
  }

  .dropdown-menu {
    max-height: 300px;
    background: #fff;
    overflow-y: auto;
  }
}
</style>
